import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Video } from '@/types/video';
import { formatCount, formatDuration } from '@/utils/video';

interface HeroCarouselProps {
  videos: Video[];
}

const slides = [
  {
    image:
      'https://readdy.ai/api/search-image?query=stylized%20city%20night%20skyline%20with%20deep%20red%20neon%20glow%20abstract%20artistic%20illustration%20dramatic%20lighting&width=1600&height=900&seq=ak-hero-01&orientation=landscape',
  },
  {
    image:
      'https://readdy.ai/api/search-image?query=abstract%20warm%20amber%20light%20rays%20over%20dark%20studio%20stage%20artistic%20illustration%20cinematic%20composition&width=1600&height=900&seq=ak-hero-02&orientation=landscape',
  },
  {
    image:
      'https://readdy.ai/api/search-image?query=stylized%20rose%20colored%20city%20rooftop%20at%20dusk%20abstract%20artistic%20illustration%20warm%20moody%20tones&width=1600&height=900&seq=ak-hero-03&orientation=landscape',
  },
  {
    image:
      'https://readdy.ai/api/search-image?query=abstract%20crimson%20smoke%20swirls%20on%20dark%20background%20artistic%20illustration%20cinematic%20lighting&width=1600&height=900&seq=ak-hero-04&orientation=landscape',
  },
];

export default function HeroCarousel({ videos }: HeroCarouselProps) {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const featured = videos.slice(0, 4);

  const go = useCallback((idx: number) => {
    setCurrent((idx + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') go(current - 1);
      if (e.key === 'ArrowRight') go(current + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [current, go]);

  return (
    <section className="relative h-[460px] w-full overflow-hidden md:h-[600px]">
      {slides.map((slide, idx) => (
        <div
          key={slide.image}
          className={`absolute inset-0 transition-opacity duration-700 ${
            idx === current ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={slide.image}
            alt="成人快手精选短视频推荐"
            title="成人快手精选短视频"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent" />
        </div>
      ))}

      <div className="relative z-10 flex h-full w-full items-center px-4 md:px-8">
        <div className="w-full max-w-2xl">
          <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary-400/40 bg-primary-500/20 px-3 py-1 text-xs font-medium text-primary-200">
            <i className="ri-fire-fill text-accent-400" />
            今日精选 · 18+
          </span>
          <h1 className="font-heading text-3xl font-bold leading-tight text-white md:text-5xl">
            发现属于成年人的
            <span className="text-primary-300">精彩短视频</span>
          </h1>
          <p className="mt-4 max-w-lg text-sm leading-relaxed text-foreground-600 md:text-base">
            生活、娱乐、兴趣、情感…海量成人主题短视频，随时浏览、筛选与收藏，找到你的兴趣圈。
          </p>

          {featured[current] && (
            <button
              type="button"
              onClick={() => navigate(`/detail/${featured[current].id}`)}
              className="group mt-8 flex w-full max-w-md items-center gap-4 rounded-xl border border-white/10 bg-black/40 p-3 text-left backdrop-blur-sm transition-colors hover:border-primary-500"
            >
              <img
                src={featured[current].cover}
                alt={featured[current].title}
                title={`${featured[current].title} - 成人快手`}
                className="h-16 w-28 shrink-0 rounded-lg object-cover object-top"
              />
              <div className="min-w-0 flex-1">
                <p className="mb-1 text-xs text-accent-300">正在推荐</p>
                <p className="line-clamp-2 text-sm font-medium text-white">{featured[current].title}</p>
                <p className="mt-1 text-xs text-foreground-600">
                  {featured[current].author} · {formatCount(featured[current].views)} 次播放 ·{' '}
                  {formatDuration(featured[current].duration)}
                </p>
              </div>
              <i className="ri-arrow-right-line text-xl text-white transition-transform group-hover:translate-x-1" />
            </button>
          )}
        </div>
      </div>

      {/* Controls */}
      <button
        type="button"
        onClick={() => go(current - 1)}
        className="absolute left-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/40 text-white transition-colors hover:bg-primary-500 md:left-6 md:h-11 md:w-11"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        type="button"
        onClick={() => go(current + 1)}
        className="absolute right-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/40 text-white transition-colors hover:bg-primary-500 md:right-6 md:h-11 md:w-11"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>

      <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
        {slides.map((slide, idx) => (
          <button
            key={slide.image}
            type="button"
            onClick={() => go(idx)}
            className={`h-2 rounded-full transition-all ${
              idx === current ? 'w-8 bg-primary-400' : 'w-2 bg-white/50 hover:bg-white/80'
            }`}
            aria-label={`切换到第 ${idx + 1} 张`}
          />
        ))}
      </div>
    </section>
  );
}